import * as types from 'subapps/kb/actions'
import { emptyObj, omit } from 'util/objects'
import { UPDATE_APP_DATA } from 'constants/action_types'

const defaultState = {
  bootstrapped: false,
  byId: {},
  ids: [],
  isKbsLoading: false,
  isLoading: false,
  isSaving: false,
  error: null,
  isLoaded: false,
}

const normalizeError = err => {
  if (typeof err === 'string') return err
  return (err && err.message) || String(err)
}

const reducers = {}

reducers[UPDATE_APP_DATA] = (state, action) => {
  const { knowledgeBases } = action.data

  if (!knowledgeBases || !Array.isArray(knowledgeBases)) {
    return state
  }

  const newById = {}
  const newIds = []

  knowledgeBases.forEach(kb => {
    const existingKb = state.byId[kb.id] || emptyObj
    // Preserve migration-related properties that might have been set by other actions
    newById[kb.id] = {
      ...existingKb,
      ...kb,
      // Preserve these specific migration-related properties if they exist in the current state
      // (taking precedence over the incoming data)
      migrationStatus: existingKb.migrationStatus || kb.migrationStatus,
      migrationInitiatedAt:
        existingKb.migrationInitiatedAt || kb.migrationInitiatedAt,
      migrationToken: existingKb.migrationToken || kb.migrationToken,
      migrationCutoffDate:
        existingKb.migrationCutoffDate || kb.migrationCutoffDate,
      migrationCutoffEnforced:
        existingKb.migrationCutoffEnforced || kb.migrationCutoffEnforced,
      instantDocsUrl: existingKb.instantDocsUrl || kb.instantDocsUrl,
      isReadOnly: existingKb.isReadOnly || kb.isReadOnly,
      canMigrate: existingKb.canMigrate || kb.canMigrate,
    }
    newIds.push(kb.id)
  })

  return {
    ...state,
    byId: newById,
    ids: newIds,
    isLoaded: true,
  }
}

reducers[types.BOOTSTRAP] = state => {
  return {
    ...state,
    bootstrapped: true,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASES_REQUEST] = state => {
  return {
    ...state,
    isKbsLoading: true,
    isLoaded: false,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASES_SUCCESS] = (state, action) => {
  const { data } = action

  const newById = {}
  const newIds = []

  data.forEach(kb => {
    newById[kb.id] = kb
    newIds.push(kb.id)
  })

  return {
    ...state,
    byId: newById,
    ids: newIds,
    isKbsLoading: false,
    isLoaded: true,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASES_FAILURE] = state => {
  return {
    ...state,
    isKbsLoading: false,
    isLoaded: false,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASE_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { knowledgeBase } = action.data
  const existingKb = state.byId[knowledgeBase.id] || emptyObj

  return {
    ...state,
    byId: {
      ...state.byId,
      ...{
        [knowledgeBase.id]: {
          ...existingKb,
          ...knowledgeBase,
        },
      },
    },
    isLoading: false,
  }
}

reducers[types.FETCH_KNOWLEDGE_BASE_FAILURE] = state => {
  return {
    ...state,
    isLoading: false,
  }
}

reducers[types.CREATE_KNOWLEDGE_BASE_REQUEST] = state => {
  return {
    ...state,
    isSaving: true,
    error: false,
  }
}

reducers[types.CREATE_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { createdKnowledgeBase } = action.data

  return {
    ...state,
    ids: [createdKnowledgeBase.id, ...state.ids],
    byId: {
      ...state.byId,
      [createdKnowledgeBase.id]: createdKnowledgeBase,
    },
    isSaving: false,
    error: null,
    lastCreatedKbId: createdKnowledgeBase.id,
  }
}

reducers[types.CREATE_KNOWLEDGE_BASE_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isSaving: false,
    error: normalizeError(err),
  }
}

reducers[types.UPDATE_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { updateKnowledgeBase, previousPrimaryKnowledgeBaseId } = action.payload
  const existingKb = state.byId[updateKnowledgeBase.id] || emptyObj
  const byId = {
    ...state.byId,
    [updateKnowledgeBase.id]: {
      ...existingKb,
      ...updateKnowledgeBase,
    },
  }

  if (
    state.byId[previousPrimaryKnowledgeBaseId] &&
    previousPrimaryKnowledgeBaseId !== updateKnowledgeBase.id
  ) {
    byId[previousPrimaryKnowledgeBaseId] = {
      ...state.byId[previousPrimaryKnowledgeBaseId],
      primary: false,
    }
  }

  return {
    ...state,
    byId,
    isSaving: false,
  }
}

reducers[types.REVERT_KNOWLEDGE_BASE_THEME] = (state, action) => {
  const { knowledgeBaseId } = action.data
  const existingKb = state.byId[knowledgeBaseId] || emptyObj

  return {
    ...state,
    byId: {
      ...state.byId,
      [knowledgeBaseId]: {
        ...existingKb,
        themeId: existingKb.publishedThemeId,
      },
    },
  }
}

reducers[types.DELETE_KNOWLEDGE_BASE_SUCCESS] = (state, action) => {
  const { knowledgeBaseId } = action.data

  return {
    ...state,
    byId: omit([knowledgeBaseId], state.byId),
    ids: state.ids.filter(id => id !== knowledgeBaseId),
  }
}

reducers[types.DELETE_KNOWLEDGE_BASE_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    error: normalizeError(err),
  }
}

reducers[types.START_MIGRATION_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
    error: null,
  }
}

reducers[types.START_MIGRATION_SUCCESS] = (state, action) => {
  const { knowledgeBase } = action.data
  const existingKb = state.byId[knowledgeBase.id] || emptyObj

  return {
    ...state,
    byId: {
      ...state.byId,
      [knowledgeBase.id]: {
        ...existingKb,
        migrationStatus: knowledgeBase.migrationStatus,
        migrationInitiatedAt: knowledgeBase.migrationInitiatedAt,
        migrationToken: knowledgeBase.migrationToken,
        instantDocsUrl: knowledgeBase.instantDocsUrl,
        migrationCutoffEnforced: knowledgeBase.migrationCutoffEnforced,
        isReadOnly: knowledgeBase.isReadOnly,
        canMigrate: knowledgeBase.canMigrate,
      },
    },
    isLoading: false,
    error: null,
  }
}

reducers[types.START_MIGRATION_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isLoading: false,
    error: normalizeError(err),
  }
}

reducers[types.CHECK_MIGRATION_STATUS_REQUEST] = state => {
  return {
    ...state,
    isLoading: true,
  }
}

reducers[types.CHECK_MIGRATION_STATUS_SUCCESS] = (state, action) => {
  const { data } = action
  const existingKb = state.byId[data.id] || emptyObj

  return {
    ...state,
    byId: {
      ...state.byId,
      [data.id]: {
        ...existingKb,
        migrationStatus: data.migrationStatus,
        migrationToken: data.migrationToken,
        instantDocsUrl: data.instantDocsUrl,
        migrationCutoffDate: data.migrationCutoffDate,
        isReadOnly: data.isReadOnly,
      },
    },
    isLoading: false,
  }
}

reducers[types.CHECK_MIGRATION_STATUS_FAILURE] = (state, action) => {
  const { err } = action.data

  return {
    ...state,
    isLoading: false,
    error: normalizeError(err),
  }
}

export default function reducer(state = defaultState, action) {
  const handler = reducers[action.type]
  if (handler) return handler(state, action)
  return state
}
